import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import UserLayout from '../../components/user/UserLayout';
import { ArrowLeft, QrCode, Radio, ExternalLink, Clock } from 'lucide-react';
import { apiRequest } from '../../utils/api';
import { useAuth } from '../../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

export default function ScanHistory() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { linkCode } = useParams(); // Get link code from URL
  const [scans, setScans] = useState([]);
  const [linkDetails, setLinkDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!user) {
      navigate('/');
      return;
    }
    fetchScanHistory();
  }, [user, navigate, linkCode]);

  const fetchScanHistory = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await apiRequest(`${BACKEND_URL}/api/user/scan-history/${linkCode}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        setScans(response.data.scans || []);
        setLinkDetails(response.data.link_details || null);
      } else {
        setError('Failed to fetch scan history');
      }
      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  const getScanIcon = (type) => {
    switch (type) {
      case 'qr':
        return <QrCode className="w-5 h-5 text-green-600" />;
      case 'nfc':
        return <Radio className="w-5 h-5 text-purple-600" />;
      default:
        return <ExternalLink className="w-5 h-5 text-blue-600" />;
    }
  };

  const getScanTypeLabel = (type) => {
    switch (type) {
      case 'qr':
        return 'QR Code Scan';
      case 'nfc':
        return 'NFC Tap';
      default:
        return 'Direct Link';
    }
  };

  const getScanTypeBadge = (type) => {
    switch (type) {
      case 'qr':
        return 'bg-green-100 text-green-800';
      case 'nfc':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-blue-100 text-blue-800';
    }
  };

  if (loading) {
    return (
      <UserLayout>
        <div className="p-8">
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-navy mx-auto"></div>
            <p className="text-gray-600 mt-4">Loading scan history...</p>
          </div>
        </div>
      </UserLayout>
    );
  }

  return (
    <UserLayout>
      <div className="p-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/user/review-standees')}
            className="flex items-center gap-2 text-gray-600 hover:text-navy mb-4 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            Back to Review Standees
          </button>
          <h1 className="text-3xl font-extrabold text-navy">Scan History</h1>
          {linkDetails && (
            <div className="mt-3 flex items-center gap-3">
              <p className="text-gray-600">{linkDetails.display_name || 'Review Card'}</p>
              <span className="text-gray-400">•</span>
              <a
                href={linkDetails.redirect_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:underline text-sm flex items-center gap-1"
              >
                {linkDetails.redirect_url}
                <ExternalLink className="w-3 h-3" />
              </a>
            </div>
          )}
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg mb-6">
            {error}
          </div>
        )}

        {/* Summary Stats */}
        {linkDetails && (
          <div className="grid md:grid-cols-3 gap-4 mb-8">
            <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
              <p className="text-sm text-gray-600 mb-1">Total Scans</p>
              <p className="text-3xl font-bold text-navy">{linkDetails.clicks || 0}</p>
            </div>
            <div className="bg-green-50 rounded-xl p-6 border border-green-200">
              <div className="flex items-center gap-2 mb-1">
                <QrCode className="w-4 h-4 text-green-600" />
                <p className="text-sm text-green-700 font-semibold">QR Scans</p>
              </div>
              <p className="text-3xl font-bold text-green-800">{linkDetails.qr_scans || 0}</p>
            </div>
            <div className="bg-purple-50 rounded-xl p-6 border border-purple-200">
              <div className="flex items-center gap-2 mb-1">
                <Radio className="w-4 h-4 text-purple-600" />
                <p className="text-sm text-purple-700 font-semibold">NFC Taps</p>
              </div>
              <p className="text-3xl font-bold text-purple-800">{linkDetails.nfc_taps || 0}</p>
            </div>
          </div>
        )}

        {/* Scan History Table */}
        {scans.length > 0 ? (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Time
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Redirected To
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {scans.map((scan, index) => (
                    <tr key={index} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2 text-sm text-gray-900">
                          <Clock className="w-4 h-4 text-gray-400" />
                          <span>{new Date(scan.timestamp).toLocaleString()}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          {getScanIcon(scan.scan_type)}
                          <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getScanTypeBadge(scan.scan_type)}`}>
                            {getScanTypeLabel(scan.scan_type)}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <a
                          href={scan.redirect_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:underline text-sm flex items-center gap-1"
                        >
                          <span className="truncate max-w-md">{scan.redirect_url}</span>
                          <ExternalLink className="w-3 h-3 flex-shrink-0" />
                        </a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-2xl shadow-sm p-12 text-center border border-gray-200">
            <Clock className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-navy mb-2">No Scans Yet</h3>
            <p className="text-gray-600">
              Scan history will appear here once your card has been scanned.
            </p>
          </div>
        )}
      </div>
    </UserLayout>
  );
}
